// src/pages/ShopListPage.js - Updated with improved filter handling
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Layout, Row, Col, Spin, Alert, Radio, Button, Empty, Typography, Space } from 'antd'; // Import Ant Design components
import LocationSearchBox from '../components/LocationSearchBox';
import FilterOptions from '../components/FilterOptions';
import ShopList from '../components/ShopList';
import MapView from '../components/MapView';
import { searchShops } from '../services/shopService';
import { ensureTestShops } from '../utils/firebase-utils';
import { EditOutlined, UserAddOutlined } from '@ant-design/icons'; // Import icons
// import './ShopListPage.css'; // Remove custom CSS

const { Content } = Layout;
const { Title } = Typography;

const ShopListPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const debounceTimeoutRef = useRef(null); // Ref für Debounce Timer

    // Extract search parameters from URL
    const [searchLocation, setSearchLocation] = useState(queryParams.get('location') || '');
    const [shops, setShops] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isMapView, setIsMapView] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // Enhanced filters state with additional options
    const [filters, setFilters] = useState(() => { // Initialisiere state einmalig aus URL
        const params = new URLSearchParams(window.location.search);
        return {
            open: params.get('open') === 'true',
            distance: parseInt(params.get('distance') || '10'),
            brands: params.getAll('brand') || [],
            minRating: parseInt(params.get('rating') || '0'),
            shopTypes: {
                premium: params.get('premium') === 'true',
                verified: params.get('verified') === 'true'
            }
        };
    });

    // Toggle filter visibility on mobile
    const toggleFilter = () => {
        setIsFilterOpen(!isFilterOpen);
    };

    // Load shops with given filters
    const loadShops = useCallback(async (locationToLoad, filtersToUse) => {
        if (!locationToLoad) {
             setLoading(false);
             setShops([]); // Keine Location -> Keine Shops
            return;
        }
        try {
            setLoading(true);
            setError(null);
            console.log('ShopListPage: Loading shops with location:', locationToLoad);
            console.log('ShopListPage: Filters:', filtersToUse);
            // await ensureTestShops(); // Kann ggf. entfernt/optimiert werden, wenn nicht nötig
            const response = await searchShops({
                location: locationToLoad,
                open: filtersToUse.open,
                brands: filtersToUse.brands,
                distance: filtersToUse.distance,
                minRating: filtersToUse.minRating,
                plan: filtersToUse.shopTypes?.premium ? 'premium' : undefined,
                isVerified: filtersToUse.shopTypes?.verified || undefined
            });
            if (response.success) {
                setShops(response.data || []);
            } else {
                setError(response.message || 'Fehler beim Laden der Shops');
            }
        } catch (err) {
            console.error('ShopListPage: Error loading shops:', err);
            setError('Fehler beim Laden der Shops. Bitte versuchen Sie es später erneut.');
        } finally {
            setLoading(false);
        }
    }, []); // loadShops selbst hat keine Abhängigkeiten außer der Service-Funktion

    // URL Update Funktion bleibt
    const updateUrlWithFilters = useCallback((searchLoc, filtersToUse) => {
        if (!searchLoc) return;
        const params = new URLSearchParams();
        params.set('location', searchLoc);
        if (filtersToUse.open) params.set('open', 'true');
        if (filtersToUse.distance && filtersToUse.distance !== 10) params.set('distance', filtersToUse.distance.toString());
        if (filtersToUse.minRating > 0) params.set('rating', filtersToUse.minRating.toString());
        if (filtersToUse.shopTypes?.premium) params.set('premium', 'true');
        if (filtersToUse.shopTypes?.verified) params.set('verified', 'true');
        filtersToUse.brands.forEach(brand => params.append('brand', brand));
        navigate(`/shops?${params.toString()}`, { replace: true });
    }, [navigate]);

    // Effekt für initiales Laden und Reaktion auf Standortänderung
    useEffect(() => {
        const initialFilters = {
             open: queryParams.get('open') === 'true',
             distance: parseInt(queryParams.get('distance') || '10'),
             brands: queryParams.getAll('brand') || [],
             minRating: parseInt(queryParams.get('rating') || '0'),
             shopTypes: {
                 premium: queryParams.get('premium') === 'true',
                 verified: queryParams.get('verified') === 'true'
             }
         };
         setFilters(initialFilters);
         loadShops(searchLocation, initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchLocation, location.search]); // Nur auf Standort/URL reagieren

    // Effekt für Debounced Filter-Updates
    useEffect(() => {
        // Beim ersten Rendern oder wenn kein Standort, nicht debounce/updaten
        if (!searchLocation) return;

        // Timer löschen, wenn Filter sich ändern
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        // Neuen Timer starten
        debounceTimeoutRef.current = setTimeout(() => {
            console.log('Debounce timer abgelaufen, lade Shops und update URL...');
            loadShops(searchLocation, filters);
            updateUrlWithFilters(searchLocation, filters);
        }, 500); // 500ms warten nach letzter Filteränderung

        // Cleanup-Funktion bei Unmount oder Änderung der Deps
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [filters, searchLocation, loadShops, updateUrlWithFilters]); // Reagiert auf Filter- und Standortänderungen

    // Handler for location search
    const handleSearch = (newLocation) => {
        if (newLocation && newLocation !== searchLocation) {
            setSearchLocation(newLocation);
            // URL wird durch den Debounce-Effekt aktualisiert, wenn sich searchLocation ändert
            // updateUrlWithFilters(newLocation, filters); // Nicht mehr hier aufrufen
        } else if (!newLocation) {
            // Wenn Suche gelöscht wird
            setSearchLocation('');
             setShops([]); // Liste leeren
            navigate('/shops', { replace: true }); // URL zurücksetzen
        }
    };

    // Handler for filter changes - setzt nur den State
    const handleFilterChange = (newFilters) => {
        setFilters(prevFilters => ({ ...prevFilters, ...newFilters }));
        // Das Laden und URL-Update wird durch den Debounce-Effekt ausgelöst
    };

    // Generate meta tags for SEO
    const generateMetaTags = () => {
        let title = searchLocation
            ? `Nikotinpouches (Snus) in ${searchLocation} kaufen | Shops & Läden | Snus Finder`
            : 'Nikotinpouches Verzeichnis Deutschland | Shops & Läden finden | Snus Finder';

        let description = searchLocation
            ? `Finde Shops und Kioske für Nikotinpouches (Snus Alternative) in ${searchLocation}. Adressen, Öffnungszeiten & Sortiment auf Snus Finder Deutschland.`
            : 'Das größte Verzeichnis für Nikotinpouches (Snus Alternativen) in Deutschland. Finde Läden und Kioske in deiner Nähe auf Snus Finder.';

        // Remove filter details from meta description (keep commented out as it was)
        /*
        if (searchLocation && (filters.open || filters.brands.length > 0 || filters.minRating > 0 || filters.shopTypes?.premium || filters.shopTypes?.verified)) {
            description += ' Gefiltert nach:';
            if (filters.open) description += ' geöffneten Shops,';
            if (filters.minRating > 0) description += ` ${filters.minRating}+ Sterne Bewertung,`;
            if (filters.shopTypes?.premium) description += ' Premium-Shops,';
            if (filters.shopTypes?.verified) description += ' Verifizierten Shops,';
            if (filters.brands.length > 0) description += ` Marken (${filters.brands.join(', ')}),`;
            description = description.replace(/,$/, '.');
        }
        */

        return { title, description };
    };

    // Generate structured data for ItemList
    const generateStructuredData = useCallback(() => {
        if (!shops || shops.length === 0) {
            return null;
        }

        const itemListElement = shops.map((shop, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
                // Use Store or OnlineStore based on your shop data structure
                "@type": shop.category === 'Online Shop' ? 'OnlineStore' : 'Store',
                "name": shop.name,
                // Use the canonical URL for the detail page
                "url": `https://www.snus-finder.de/shops/${shop._id}`, // Assuming shop._id exists
                // Optionally add a brief address or description if available
                "address": shop.address ? {
                    "@type": "PostalAddress",
                    "addressLocality": shop.city,
                    "streetAddress": shop.address
                } : undefined
            }
        }));

        // Use the canonical URL for the list page itself
        const listUrl = `https://www.snus-finder.de/shops${location.search}`; // Includes query params like ?location=...

        return {
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": searchLocation ? `Nikotinpouches (Snus) Shops in ${searchLocation}` : "Nikotinpouches (Snus) Shops Deutschland",
            "description": searchLocation ? `Liste von Shops und Läden für Nikotinpouches (Snus Alternativen) in ${searchLocation}.` : "Übersicht über Verkaufsstellen für Nikotinpouches (Snus Alternativen) in Deutschland.",
            "url": listUrl,
            "numberOfItems": shops.length,
            "itemListElement": itemListElement
        };
    }, [shops, searchLocation, location.search]); // Dependencies: shops array, searchLocation for name/desc, location.search for URL

    const metaTags = generateMetaTags();
    const structuredDataJSON = generateStructuredData(); // Generate the JSON-LD data

    return (
        <Layout>
            <Helmet>
                <title>{metaTags.title}</title>
                <meta name="description" content={metaTags.description} />
                {/* Add canonical link for the list page */}
                <link rel="canonical" href={`https://www.snus-finder.de/shops${location.search}`} />

                {/* Add ItemList Structured Data */}
                {structuredDataJSON && (
                    <script type="application/ld+json">
                        {JSON.stringify(structuredDataJSON)}
                    </script>
                )}
            </Helmet>
            <Content style={{ padding: '20px 50px' }}> {/* Adjusted padding */}
                <Row justify="center" style={{ marginBottom: '20px' }}>
                    <Col xs={24} md={18} lg={12}>
                        <Title level={1} style={{ textAlign: 'center', marginBottom: '20px' }}>
                            {searchLocation
                                ? `Snus Shops in ${searchLocation}`
                                : 'Finde Snus-Verkaufsstellen'
                            }
                        </Title>
                        {/* Optional: Add introductory text when location is set */}
                        {searchLocation && (
                            <Typography.Paragraph style={{ textAlign: 'center', marginBottom: '20px' }}>
                                Hier findest du eine Liste von Läden, Kiosken und Tankstellen in <Typography.Text strong>{searchLocation}</Typography.Text>, die Snus oder Nikotinbeutel anbieten. Nutze die Filter, um deine Suche zu verfeinern.
                            </Typography.Paragraph>
                        )}
                        <LocationSearchBox
                            initialValue={searchLocation}
                            onSearch={handleSearch}
                            isLoading={loading && !shops.length} // Show loading in search box only initially
                        />
                        {error && !loading && ( // Show main error only when not loading something new
                            <Alert message={error} type="error" showIcon style={{ marginTop: '10px' }} />
                        )}
                    </Col>
                </Row>

                {error && <Alert message={error} type="error" showIcon style={{ marginBottom: '20px' }} />}

                {/* Filter toggle button for mobile */}
                <Row justify="center" style={{ marginBottom: '10px' }}>
                    <Col xs={24} md={0}> {/* Only show on mobile */}
                        <Button onClick={toggleFilter} block>
                            {isFilterOpen ? 'Filter schließen' : 'Filter öffnen'}
                        </Button>
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    {/* Filter Section - always visible on md and up, toggleable on xs */}
                    <Col xs={24} md={6} style={{ display: isFilterOpen || window.innerWidth >= 768 ? 'block' : 'none' }}>
                        <FilterOptions filters={filters} onChange={handleFilterChange} />
                    </Col>

                    {/* Main Content Area (Map/List) */}
                    <Col xs={24} md={18}>
                        <Radio.Group value={isMapView ? 'map' : 'list'} onChange={(e) => setIsMapView(e.target.value === 'map')} style={{ marginBottom: '16px' }}>
                            <Radio.Button value="list">Liste</Radio.Button>
                            <Radio.Button value="map">Karte</Radio.Button>
                        </Radio.Group>

                        {loading && <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>}

                        {!loading && shops.length > 0 && (
                            isMapView
                                ? <MapView shops={shops} centerLocation={searchLocation} />
                                : <ShopList shops={shops} loading={loading} error={error} />
                        )}

                        {/* NEU: Nachricht bei keinen Ergebnissen */}
                        {!loading && shops.length === 0 && searchLocation && (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <Space direction="vertical" size="large">
                                        <Typography.Text strong style={{ fontSize: '1.1em' }}>
                                            Keine Shops für "{searchLocation}" gefunden.
                                        </Typography.Text>
                                        <Typography.Text>
                                            Kennst du einen Laden in der Nähe, der Snus oder Nikotinbeutel verkauft?
                                            Hilf uns, das Verzeichnis zu verbessern!
                                        </Typography.Text>
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            <Link to="/shop-melden">
                                                <Button type="default" icon={<EditOutlined />} block>
                                                    Shop melden
                                                </Button>
                                            </Link>
                                            <Link to="/register">
                                                <Button type="primary" icon={<UserAddOutlined />} block>
                                                    Bist du Shop-Besitzer? Hier registrieren
                                                </Button>
                                            </Link>
                                        </Space>
                                    </Space>
                                }
                            />
                        )}

                         {/* Fallback, falls keine Suche gestartet wurde */}
                        {!loading && shops.length === 0 && !searchLocation && (
                             <Empty
                                 image={Empty.PRESENTED_IMAGE_SIMPLE}
                                 description="Bitte gib einen Ort ein, um nach Shops zu suchen."
                             />
                         )}
                    </Col>
                </Row>
            </Content>
        </Layout>
    );
};

export default ShopListPage;